import {
  Cog6ToothIcon,
  QuestionMarkCircleIcon,
  EllipsisVerticalIcon,
  MagnifyingGlassMinusIcon,
  MagnifyingGlassPlusIcon,
} from '@heroicons/react/24/outline';
import { useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/store';
import {
  getZoomInFromLevel,
  getZoomOutFromLevel,
  ZOOM_LEVELS,
  canZoomToLevel,
  estimateIntervalCount,
  MAX_ZOOM_COLUMNS,
} from '../../utils/zoom';
import type { MilestoneMapInterval } from '@mm-shared/types/MilestoneMap1';
import {
  setTreeView,
  setSidebarTreeView,
  setCrosshairs,
  setRelativesView,
  setSettingsView,
  setPlaylistMode,
  setHierarchyZoomMode,
  setHelpDialog,
  setShowStatusCodes,
  setAddExistingObjectives,
  setStatusCellDisplay,
  setZoomLevel,
  resetMapZoomLevel,
} from '../../store/map3UISlice';
import { selectCurrentZoomLevel, selectEffectiveZoomLevel } from '../../store/selectors/zoomSelectors';
import {
  DialogDescription,
  DialogTitle,
  DialogBody,
  DialogActions,
} from '../../components_catalyst/dialog';
import { Dialog } from '../../components_catalyst/dialog';
import { Button } from '../../components_catalyst/button';
import { VideoEmbed } from './VideoEmbed1';
import type { StatusCellDisplayMode } from '../../components_misc/StatusCellDisplayIcon';
import { Dropdown, DropdownButton } from '../../components_catalyst/dropdown';
import { MapOptionsDropDownMenu } from '../../map_dependencies/components/MapOptionsDropDownMenu';
import { Map3ControlsDropdownMenu } from './Map3ControlsDropdownMenu';

export function Map3Controls() {
  const { mapShortId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  // Get the milestone map.
  const milestoneMap = useSelector((state: RootState) => {
    try {
      return state.map3Data.milestoneMaps.find((map) => map.shortId === mapShortId);
    } catch (error) {
      console.error('Error finding milestone map:', error);
      return undefined;
    }
  });

  // Derive view state from ?view query param (not pathname)
  const currentView = searchParams.get('view');
  const isRelativesView = currentView === 'relatives';
  const isTreeView = currentView === 'treeview';
  const isDependenciesFormView = currentView === 'dependencies';
  const isSettingsView = currentView === 'settings';

  // Get view mode from Redux state
  const viewMode = useSelector((state: RootState) => state.map3UI.mapControls.relatives.viewMode);
  const isSharedTimelineMode = viewMode === 'sharedTimeline';

  // Add check for help videos with proper null check
  const hasHelpVideos = milestoneMap?.helpVideos ? milestoneMap.helpVideos.length > 0 : false;

  // Helper to set ?view param while preserving all other params (e.g. ?zoomLevel)
  const setView = (view: string | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (view) {
        next.set('view', view);
      } else {
        next.delete('view');
      }
      return next;
    });
  };

  const {
    enableTreeView,
    enableSidebarTreeView,
    enableCrosshairs,
    enableSettingsView,
    enablePlaylistMode,
    enableHierarchyZoomMode,
    enableHelpDialog,
    showStatusCodes,
    enableAddExistingObjectives,
    toggleStatusCellDisplay,
  } = useSelector((state: RootState) => state.map3UI.mapControls);

  // Get current zoom level from Redux (null = use map default)
  const currentZoomLevel = useSelector(selectCurrentZoomLevel);
  // Get effective zoom level (current or map's base interval)
  const effectiveZoomLevel = useSelector((state: RootState) =>
    selectEffectiveZoomLevel(state, milestoneMap?.interval || 'weeks')
  );

  // Sync URL parameter to Redux on mount and when URL changes
  useEffect(() => {
    const urlZoomLevel = searchParams.get('zoomLevel') as MilestoneMapInterval | null;

    // If URL has a zoom parameter
    if (urlZoomLevel) {
      // Validate it's a valid zoom level
      if (ZOOM_LEVELS.includes(urlZoomLevel)) {
        // Only update Redux if it differs from current state to avoid loops
        if (urlZoomLevel !== currentZoomLevel) {
          dispatch(setZoomLevel(urlZoomLevel));
        }
      } else {
        // Invalid zoom level in URL - remove it
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('zoomLevel');
        setSearchParams(newParams, { replace: true });
      }
    } else {
      // No zoom parameter in URL - reset to map default if Redux has a value
      if (currentZoomLevel !== null) {
        dispatch(resetMapZoomLevel());
      }
    }
  }, [searchParams, currentZoomLevel, dispatch, setSearchParams]);

  // Sync Redux UI state with ?view query param (handles direct URL navigation / bookmarks)
  useEffect(() => {
    dispatch(setSettingsView(isSettingsView));
    dispatch(setRelativesView(isRelativesView));
    dispatch(setTreeView(isTreeView));
  }, [isSettingsView, isRelativesView, isTreeView, dispatch]);

  const disableAllControls = (options?: { preserveZoom?: boolean }) => {
    dispatch(setTreeView(false));
    dispatch(setCrosshairs(false));
    dispatch(setRelativesView(false));
    dispatch(setSettingsView(false));
    dispatch(setPlaylistMode(false));
    dispatch(setHelpDialog(false));

    // Only reset zoom if not preserving it
    if (!options?.preserveZoom) {
      // Reset zoom level to map default when changing views
      dispatch(resetMapZoomLevel());
      // Clear view and zoom parameters
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('view');
        next.delete('zoomLevel');
        return next;
      });
    }
  };

  const handleRelativesToggle = () => {
    if (!isRelativesView) {
      disableAllControls({ preserveZoom: true });
      setView('relatives');
      dispatch(setRelativesView(true));
      // Set display to full mode when enabling relatives view
      dispatch(setStatusCellDisplay('status_results2_comments'));
    } else {
      disableAllControls({ preserveZoom: true });
      setView(null);
    }
  };

  const handleSettingsToggle = () => {
    if (!enableSettingsView) {
      disableAllControls();
      dispatch(setSettingsView(true));
      setView('settings');
    } else {
      dispatch(setSettingsView(false));
      setView(null);
    }
  };

  const handlePlaylistToggle = () => {
    if (!enablePlaylistMode) {
      disableAllControls();
      dispatch(setPlaylistMode(true));
      setView('playlists');
    } else {
      dispatch(setPlaylistMode(false));
      setView(null);
    }
  };

  const handleHelpToggle = () => {
    if (!enableHelpDialog) {
      disableAllControls();
      dispatch(setHelpDialog(true));
      setView('help-videos');
    } else {
      dispatch(setHelpDialog(false));
      setView(null);
    }
  };

  const handleTreeViewToggle = () => {
    if (!isTreeView) {
      disableAllControls();
      // Disable sidebar tree view when enabling full tree view (mutual exclusion)
      dispatch(setSidebarTreeView(false));
      dispatch(setTreeView(true));
      setView('treeview');
    } else {
      dispatch(setTreeView(false));
      setView(null);
    }
  };

  /**
   * Note: Not disabled with disableAllControls(), simplest way
   * around weird edge cases.
   */
  const handleSidebarTreeViewToggle = () => {
    if (!enableSidebarTreeView) {
      // Disable full tree view when enabling sidebar (mutual exclusion)
      if (isTreeView) {
        handleTreeViewToggle();
      }
      dispatch(setSidebarTreeView(true));
      // Stay on current page - sidebar persists across views
    } else {
      dispatch(setSidebarTreeView(false));
    }
  };

  const handleCrosshairsToggle = () => {
    if (!enableCrosshairs) {
      disableAllControls();
      dispatch(setCrosshairs(true));
    } else {
      dispatch(setCrosshairs(false));
    }
  };

  const handleShowStatusCodesToggle = () => {
    dispatch(setShowStatusCodes(!showStatusCodes));
  };

  const handleAddExistingObjectivesToggle = () => {
    dispatch(setAddExistingObjectives(!enableAddExistingObjectives));
  };

  const handleSetStatusCellDisplayMode = (mode: StatusCellDisplayMode) => {
    // If relatives view is enabled and selecting a mode other than the full mode,
    // exit relatives view first
    if (isRelativesView && mode !== 'status_results2_comments') {
      // Note: Map3Relatives needs the full display mode to work properly.
      // Switching to a simpler mode requires exiting relatives view.
      disableAllControls();
    }
    dispatch(setStatusCellDisplay(mode));
  };

  /**
   * Sets the interaction mode for objective rows.
   * Ensures mutual exclusivity: only one mode can be active at a time.
   * - 'drag': Default mode with drag handles for reordering
   * - 'playlist': Shows playlist play buttons on child objectives
   * - 'zoom': Shows hierarchy zoom buttons for navigation between maps
   */
  const handleSetInteractionMode = (mode: 'drag' | 'playlist' | 'zoom') => {
    switch (mode) {
      case 'drag':
        dispatch(setPlaylistMode(false));
        dispatch(setHierarchyZoomMode(false));
        break;
      case 'playlist':
        dispatch(setPlaylistMode(true));
        dispatch(setHierarchyZoomMode(false));
        break;
      case 'zoom':
        dispatch(setPlaylistMode(false));
        dispatch(setHierarchyZoomMode(true));
        break;
    }
  };

  return (
    <aside aria-label="Map Toolbar and Settings">
      <div id="Map3Controls" className="text-sm flex justify-between w-full">
        <div className="flex gap-2">
          <Dropdown>
            <DropdownButton as={Button} plain aria-label="Map controls">
              <EllipsisVerticalIcon className="h-6 w-6" aria-hidden="true" />
            </DropdownButton>
            <Map3ControlsDropdownMenu
              featureFlags={milestoneMap?.featureFlags}
              toggleStates={{
                showStatusCodes,
                statusCellDisplayMode: toggleStatusCellDisplay,
                enableCrosshairs,
                enablePlaylistMode,
                enableHierarchyZoomMode,
                isRelativesView,
                enableAddExistingObjectives,
                enableTreeView,
                enableSidebarTreeView,
              }}
              handlers={{
                onToggleStatusCodes: handleShowStatusCodesToggle,
                onSetStatusCellDisplayMode: handleSetStatusCellDisplayMode,
                onToggleCrosshairs: handleCrosshairsToggle,
                onSetInteractionMode: handleSetInteractionMode,
                onToggleRelativesView: handleRelativesToggle,
                onToggleAddExistingObjectives: handleAddExistingObjectivesToggle,
                onToggleTreeView: handleTreeViewToggle,
                onToggleSidebarTreeView: handleSidebarTreeViewToggle,
              }}
            />
          </Dropdown>
        </div>
        <div className="flex-grow"></div>
        <div className="flex gap-2">
          {/* Time zoom controls - only shown when hierarchy zoom mode is enabled */}
          {enableHierarchyZoomMode && (
            <div className="flex mr-4 border-r pr-4">
              <button
                onClick={() => {
                  const nextZoomLevel = getZoomOutFromLevel(effectiveZoomLevel);
                  if (nextZoomLevel) {
                    console.debug('[Zoom] Zooming out:', {
                      from: effectiveZoomLevel,
                      to: nextZoomLevel,
                      mapUuid: milestoneMap?.uuid,
                    });
                    // Update Redux state
                    dispatch(setZoomLevel(nextZoomLevel));
                    // Update URL parameter for backward compatibility with Map3Relatives
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set('zoomLevel', nextZoomLevel);
                    setSearchParams(newParams);
                  }
                }}
                className="px-2 py-2 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:cursor-not-allowed disabled:hover:bg-white [&:disabled>svg]:opacity-50"
                title={
                  getZoomOutFromLevel(effectiveZoomLevel) === null
                    ? 'Already at coarsest zoom level (years)'
                    : 'Zoom out timeline'
                }
                disabled={getZoomOutFromLevel(effectiveZoomLevel) === null}
              >
                <span className="sr-only">Zoom out timeline</span>
                <MagnifyingGlassMinusIcon className="h-5 w-5 text-gray-700" aria-hidden="true" />
              </button>
              <button
                onClick={() => {
                  console.debug('[Zoom] Resetting zoom:', {
                    from: effectiveZoomLevel,
                    to: milestoneMap?.interval,
                    mapUuid: milestoneMap?.uuid,
                  });
                  // Reset zoom level to map's default
                  dispatch(resetMapZoomLevel());
                  // Remove zoomLevel URL parameter
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete('zoomLevel');
                  setSearchParams(newParams);
                }}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-l-0 border-gray-300 hover:bg-gray-50 cursor-pointer"
                title="Reset to default"
              >
                {effectiveZoomLevel}
              </button>
              <button
                onClick={() => {
                  const nextZoomLevel = getZoomInFromLevel(effectiveZoomLevel);
                  if (nextZoomLevel && milestoneMap && canZoomToLevel(milestoneMap, nextZoomLevel)) {
                    console.debug('[Zoom] Zooming in:', {
                      from: effectiveZoomLevel,
                      to: nextZoomLevel,
                      mapUuid: milestoneMap?.uuid,
                    });
                    // Update Redux state
                    dispatch(setZoomLevel(nextZoomLevel));
                    // Update URL parameter for backward compatibility with Map3Relatives
                    const newParams = new URLSearchParams(searchParams);
                    newParams.set('zoomLevel', nextZoomLevel);
                    setSearchParams(newParams);
                  }
                }}
                className="px-2 py-2 bg-white border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-50 disabled:cursor-not-allowed disabled:hover:bg-white [&:disabled>svg]:opacity-50"
                title={(() => {
                  const nextZoomLevel = getZoomInFromLevel(effectiveZoomLevel);
                  if (nextZoomLevel === null) {
                    return 'Already at finest zoom level (hours)';
                  }
                  if (milestoneMap && !canZoomToLevel(milestoneMap, nextZoomLevel)) {
                    const estimatedColumns = estimateIntervalCount(
                      new Date(milestoneMap.startDate),
                      new Date(milestoneMap.endDate),
                      nextZoomLevel,
                    );
                    return `Cannot zoom to ${nextZoomLevel}: would create ~${estimatedColumns} columns (max ${MAX_ZOOM_COLUMNS})`;
                  }
                  return 'Zoom in timeline';
                })()}
                disabled={
                  getZoomInFromLevel(effectiveZoomLevel) === null ||
                  (milestoneMap
                    ? !canZoomToLevel(milestoneMap, getZoomInFromLevel(effectiveZoomLevel)!)
                    : false)
                }
              >
                <span className="sr-only">Zoom in timeline</span>
                <MagnifyingGlassPlusIcon className="h-5 w-5 text-gray-700" aria-hidden="true" />
              </button>
            </div>
          )}
          {/* Map options menu - shown when in relatives/dependencies view */}
          {isRelativesView && (
            <Dropdown>
              <DropdownButton
                as={Button}
                plain
                aria-label="Map options"
                className="cursor-pointer"
              >
                <EllipsisVerticalIcon className="h-6 w-6" aria-hidden="true" />
              </DropdownButton>
              <MapOptionsDropDownMenu disabled={false} />
            </Dropdown>
          )}
          <div className="flex gap-2">
            {hasHelpVideos && (
              <button
                onClick={handleHelpToggle}
                className={`p-2 rounded ${enableHelpDialog ? 'bg-gray-200' : 'bg-white'}`}
                title="Toggle help videos"
                aria-pressed={enableHelpDialog}
                aria-expanded={enableHelpDialog}
              >
                <span className="sr-only">Toggle help videos</span>
                <QuestionMarkCircleIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            )}
            <button
              onClick={handleSettingsToggle}
              className={`p-2 rounded ${enableSettingsView ? 'bg-gray-200' : 'bg-white'}`}
              title="Toggle settings"
              aria-pressed={enableSettingsView}
            >
              <span className="sr-only">Toggle settings</span>
              <Cog6ToothIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
      <Dialog open={enableHelpDialog} onClose={handleHelpToggle}>
        {/* TODO: Make these more contextual, as part of AddEditDelete action bar. */}
        <DialogTitle>Help Videos</DialogTitle>
        <DialogDescription>Use the videos below to help you get started.</DialogDescription>
        <DialogBody>
          <div className="flex flex-col gap-6">
            {milestoneMap?.helpVideos?.map((video) => (
              <div key={video.url}>
                <h3 className="font-semibold text-lg">{video.title}</h3>
                <VideoEmbed url={video.url} />
                {video.description && <p className="text-gray-600">{video.description}</p>}
              </div>
            ))}
          </div>
        </DialogBody>
        <DialogActions>
          <Button onClick={handleHelpToggle}>Close</Button>
        </DialogActions>
      </Dialog>
    </aside>
  );
}
